/**
 * @file Shared React component — components/DataVisualisation/boxPlotCommon.ts.
 */

export type TimeSlicer = "10 Years" | "5 Years" | "1 Year" | "6 Months" | "None";

/** Exported const — TIMELINE ANCHOR YEAR. */
export const TIMELINE_ANCHOR_YEAR = 1996;
/** Exported const — DEMO MATERIALS. */
export const DEMO_MATERIALS = ["OPC", "PPC", "PSC"] as const;
/** Exported const — DEMO PROCESSES. */
export const DEMO_PROCESSES = ["Wet Process", "Dry Process"] as const;

/**
 * Calendar range bucket width for "5 Years" / "10 Years" slicers (anchored at TIMELINE_ANCHOR_YEAR).
 */
export function rangeBucketYearsForSlicer(slicer: TimeSlicer): number {
  if (slicer === "5 Years") return 5;
  if (slicer === "10 Years") return 10;
  return 10;
}

/** Exported function — to Year. */
export function toYear(value?: string | null): number | null {
  if (!value) return null;
  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) ? n : null;
}

function firstPubField(p: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = p[k];
    if (v != null && String(v).trim() !== "") return String(v);
  }
  return undefined;
}

function monthStringToIndex(s?: string): number {
  if (s == null || String(s).trim() === "") return 0;
  const raw = String(s).trim();
  const n = Number.parseInt(raw, 10);
  if (Number.isFinite(n) && n >= 1 && n <= 12) return n - 1;

  const monthNames = new Map<string, number>([
    ["jan", 0],
    ["january", 0],
    ["feb", 1],
    ["february", 1],
    ["mar", 2],
    ["march", 2],
    ["apr", 3],
    ["april", 3],
    ["may", 4],
    ["jun", 5],
    ["june", 5],
    ["jul", 6],
    ["july", 6],
    ["aug", 7],
    ["august", 7],
    ["sep", 8],
    ["sept", 8],
    ["september", 8],
    ["oct", 9],
    ["october", 9],
    ["nov", 10],
    ["november", 10],
    ["dec", 11],
    ["december", 11],
  ]);
  const normalized = raw.toLowerCase().replace(/\./g, "");
  return monthNames.get(normalized) ?? 0;
}

function dayStringToDay(s?: string): number {
  if (s == null || String(s).trim() === "") return 1;
  const n = Number.parseInt(s, 10);
  if (!Number.isFinite(n) || n < 1 || n > 31) return 1;
  return n;
}

/**
 * Timestamp from publication block (journal / EPD date fields). Returns null if no usable year.
 */
export function publicationTimeMs(publication: unknown): number | null {
  if (!publication || typeof publication !== "object") return null;
  const p = publication as Record<string, unknown>;

  const pubYear = toYear(
    firstPubField(p, ["publicationYear", "publication_year"])
  );
  if (pubYear !== null) {
    const m = monthStringToIndex(
      firstPubField(p, ["publicationMonth", "publication_month"])
    );
    const day = dayStringToDay(firstPubField(p, ["publicationDay", "publication_day"]));
    const t = new Date(pubYear, m, day).getTime();
    return Number.isFinite(t) ? t : null;
  }

  const epdYear = toYear(
    firstPubField(p, ["epdPublicationYear", "epd_publication_year"])
  );
  if (epdYear !== null) {
    const m = monthStringToIndex(
      firstPubField(p, ["epdPublicationMonth", "epd_publication_month"])
    );
    const day = dayStringToDay(firstPubField(p, ["epdPublicationDay", "epd_publication_day"]));
    const t = new Date(epdYear, m, day).getTime();
    return Number.isFinite(t) ? t : null;
  }

  const legacyYear = toYear(firstPubField(p, ["year", "Year"]));
  if (legacyYear !== null) {
    return new Date(legacyYear, 0, 1).getTime();
  }

  return null;
}

/** Exported function — dataset Time Ms. */
export function datasetTimeMs(d: {
  createdAt?: string | null;
  publication?: unknown;
}): number | null {
  // 1) Publication date (Y-M-D from publication or EPD fields)
  const pubMs = publicationTimeMs(d.publication);
  if (pubMs !== null && Number.isFinite(pubMs)) return pubMs;

  // 2) Dataset / submission created date
  if (d.createdAt) {
    const t = new Date(d.createdAt).getTime();
    if (Number.isFinite(t)) return t;
  }

  return null;
}

/** End-of-period label for 6-month buckets: H1 → Jun YYYY, H2 → Dec YYYY. */
export function sixMonthBucketLabel(
  year: number,
  half: 1 | 2
): { label: string; sortMs: number } {
  const monthIndex = half === 1 ? 5 : 11;
  const monthName = half === 1 ? "Jun" : "Dec";
  return {
    label: `${monthName} ${year}`,
    sortMs: new Date(year, monthIndex, 1).getTime(),
  };
}

/** Exported function — bucket For Time. */
export function bucketForTime(
  ms: number | null,
  slicer: TimeSlicer
): { label: string; sortMs: number } {
  if (slicer === "None") return { label: "All", sortMs: 0 };

  if (ms == null || !Number.isFinite(ms)) {
    const yearNow = new Date().getFullYear();
    if (slicer === "6 Months") return sixMonthBucketLabel(yearNow, 1);
    if (slicer === "1 Year") return { label: `${yearNow}`, sortMs: new Date(yearNow, 0, 1).getTime() };
    const step = rangeBucketYearsForSlicer(slicer);
    const bucketStart =
      TIMELINE_ANCHOR_YEAR +
      Math.floor((yearNow - TIMELINE_ANCHOR_YEAR) / step) * step;
    const bucketEnd = bucketStart + step - 1;
    return { label: `${bucketStart}-${bucketEnd}`, sortMs: new Date(bucketStart, 0, 1).getTime() };
  }

  const d = new Date(ms);
  const year = d.getFullYear();
  const month = d.getMonth(); // 0-11

  if (slicer === "6 Months") {
    return sixMonthBucketLabel(year, month < 6 ? 1 : 2);
  }

  if (slicer === "1 Year") {
    return { label: `${year}`, sortMs: new Date(year, 0, 1).getTime() };
  }

  const step = rangeBucketYearsForSlicer(slicer);
  const bucketStart =
    TIMELINE_ANCHOR_YEAR +
    Math.floor((year - TIMELINE_ANCHOR_YEAR) / step) * step;
  const bucketEnd = bucketStart + step - 1;
  return { label: `${bucketStart}-${bucketEnd}`, sortMs: new Date(bucketStart, 0, 1).getTime() };
}

/**
 * For "10 Years" mode: add every 10-year range bucket between minYear and maxYear (inclusive)
 * so the x-axis shows contiguous blocks (including empty slots).
 */
export function expandTenYearSlicerBuckets(
  labelsWithSort: Map<string, number>,
  minYear: number,
  maxYear: number
): Map<string, number> {
  const step = rangeBucketYearsForSlicer("10 Years");
  const start =
    TIMELINE_ANCHOR_YEAR +
    Math.floor((minYear - TIMELINE_ANCHOR_YEAR) / step) * step;
  const endBucketStart =
    TIMELINE_ANCHOR_YEAR +
    Math.floor((maxYear - TIMELINE_ANCHOR_YEAR) / step) * step;
  const out = new Map(labelsWithSort);
  for (let bucketStart = start; bucketStart <= endBucketStart; bucketStart += step) {
    const bucketEnd = bucketStart + step - 1;
    const label = `${bucketStart}-${bucketEnd}`;
    const sortMs = new Date(bucketStart, 0, 1).getTime();
    if (!out.has(label)) out.set(label, sortMs);
  }
  return out;
}

/** Exported function — format Timeline Tick. */
export function formatTimelineTick(label: string): string[] {
  // Range buckets: "1996-2005" -> ["1996-", "2005"]
  const range = label.match(/^(\d{4})-(\d{4})$/);
  if (range) return [`${range[1]}-`, range[2]];

  // Six-month buckets: "Jun 2026" -> ["Jun", "2026"]
  const sixMonth = label.match(/^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{4})$/);
  if (sixMonth) return [sixMonth[1], sixMonth[2]];

  // Legacy half-year buckets: "2025-H1" -> ["Jun", "2025"]
  const half = label.match(/^(\d{4})-H([12])$/);
  if (half) return [half[2] === "1" ? "Jun" : "Dec", half[1]];

  // Year bucket: "2024"
  const year = label.match(/^\d{4}$/);
  if (year) return [label];

  // Fallback
  return [label];
}

